<template>
    <div class="segment-list-item">
        <table>
            <!-- Table header row -->
            <tr>
                <th>
                    <div class="title-wrap font-3">
                        <div class="title">
                            <Tooltip :text="tooltipText">
                                {{ name }}
                            </Tooltip>
                        </div>
                        <ul class="buttons show-on-hover">
                            <router-link class="edit-segment-link" :to="editUrl">
                                <li>
                                    <IconBase
                                        iconName="editIcon"
                                        :iconColor="consts.COLORS.PRIMARY_500"
                                        display="flex"
                                        width="15"
                                        height="15"
                                    />
                                </li>
                            </router-link>
                            <li class="delete-btn" @click="handleRemove">
                                <IconBase
                                    iconName="deleteIcon"
                                    :iconColor="consts.COLORS.PRIMARY_500"
                                    display="flex"
                                    width="12"
                                    height="15"
                                />
                            </li>
                        </ul>
                    </div>
                </th>
                <th class="font-6">Placement</th>
                <th class="font-6">Ad Settings' Exceptions</th>
            </tr>
            <!-- Table content -->
            <template v-for="(enabled, adUnit) in adUnitDelivery">
                <!-- Ad Unit exceptions -->
                <template v-if="enabled">
                    <tr v-for="(placement, index) in getPlacements(adUnit)" :key="adUnit + index">
                        <td v-if="index === 0" :rowspan="getPlacements(adUnit).length">
                            <AdUnitTitle :adUnit="adUnit" class="adunit-label"/>
                        </td>
                        <td>
                            {{ getPlacementName(placement.placementId, adUnit) }}
                        </td>
                        <td>
                            <ExceptionSettings :placement="placement"/>
                        </td>
                    </tr>
                </template>
                <!-- Delivery switched off -->
                <tr v-if="!enabled" :key="adUnit">
                    <td class="no-border-right" colspan="3">
                        <div class="delivery-disabled-container">
                            <AdUnitTitle :adUnit="adUnit" class="adunit-label"/>
                            <span class="delivery-disabled-text">Ad Delivery Switched Off</span>
                        </div>
                    </td>
                </tr>
            </template>
            <!-- No exceptions defined -->
            <tr v-if="!hasExceptions && !anyAdUnitsDeliveryDisabled">
                <td colspan="3" class="empty-row">
                    No Ad Settings Defined. Tailor your ad settings for this segment
                    <router-link :to="editUrl">here</router-link>
                </td>
            </tr>
        </table>
        <Loader :status="loading" class="segment-loader"/>
        <div v-if="disabled" class="disabled-overlay"/>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import Loader from 'components/loader/loader.vue';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import AdUnitTitle from 'components/AdUnitTitle.vue';
import consts from 'services/metaData/consts';
import ExceptionSettings from '@/pages/monetize/segments/segmentList/ExceptionSettings.vue';
import { adUnitIds } from 'services/utils/adUnitsMap';

export default {
    name: 'SegmentListItem',
    components: {
        ExceptionSettings,
        AdUnitTitle,
        IconBase,
        Loader,
        Tooltip
    },
    props: {
        name: {
            type: String,
            required: true
        },
        exceptions: {
            type: Object,
            default: () => ({})
        },
        metaData: {
            type: Object,
            default: () => ({})
        },
        conditions: {
            type: Array,
            default: () => []
        },
        placementNames: {
            type: Object,
            default: () => ({})
        },
        loading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        id: Number,
        testGroup: String,
        appKey: String
    },
    computed: {
        editUrl() {
            return `/monetize/segments/${this.appKey}/${this.id}/${this.testGroup}`;
        },
        adUnitDelivery() {
            return this.metaData.deliveryEnabled || {};
        },
        anyAdUnitsDeliveryDisabled() {
            return Object.keys(this.adUnitDelivery).some((adUnit) => !this.adUnitDelivery[adUnit]);
        },
        hasExceptions() {
            return this.exceptions && Object.keys(this.exceptions).length > 0;
        },
        tooltipText() {
            return this.conditions.map((c) => `${c.name} ${c.comparator} ${c.value}`).join('\n');
        },
        consts: () => consts
    },
    methods: {
        getPlacements(adUnit) {
            return this.exceptions[adUnit] || [];
        },
        getPlacementName(placementId, adUnit) {
            return this.placementNames[`${adUnitIds[adUnit]}_${placementId}`];
        },
        handleRemove() {
            if (!this.disabled) {
                this.$emit('remove');
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";

.segment-list-item {
    position: relative;
    border: $border;
    border-radius: $borderRadius;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, .1);
    margin: 0 0 20px;
}

.segment-loader {
    margin: 1px; // need margin, so borders could be visible
}

table {
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;

    &:hover {
        background: $light-100;

        .show-on-hover {
            display: flex;
        }
    }

    th {
        padding: 1px 13px 0;
        height: 49px;
        color: $light-800;

        &:first-child {
            padding-left: 17px;
        }
    }

    td {
        height: 41px;
        @include font(5);
        vertical-align: top;
        border-top: $border;
        border-right: $border;
        padding: 10px 10px 10px;

        &:last-child, &.no-border-right {
            border-right: none;
        }
    }

    .title-wrap {
        display: flex;
        line-height: normal;

        .title {
            color: $dark-300;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .empty-row {
        vertical-align: middle;
        text-align: center;
    }

    a {
        color: $primary;
        text-decoration: none;
    }

    .delivery-disabled-text {
        color: $negative;
    }
}

.adunit-label {
    margin-left: 8px;
}

ul.buttons {
    list-style: none;
    display: none;
    flex-direction: row;
    margin: 0 0 0 7px;
    padding: 0;

    li {
        position: relative;
        margin-right: 1px;
        padding: 0 6px;

        &:hover {
            cursor: pointer;
        }

        // buttons separator
        &.delete-btn:before {
            content: '';
            display: block;
            width: 1px;
            height: 14px;
            position: absolute;
            top: 1px;
            left: 0;
            background: $primary;
        }
    }
}

.disabled-overlay {
    position: absolute;
    z-index: 15;
    background: $white;
    opacity: 0.4;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
}

.delivery-disabled-container {
    display: flex;
    align-items: center;

    .adunit-label {
        width: 150px;
    }
}
</style>
